import React, {useEffect, useState} from "react";
import {auth, db} from "./firebase";
import {addDoc, collection, deleteDoc, doc, getDoc, getDocs, updateDoc, Timestamp} from "firebase/firestore";
import {onAuthStateChanged, signInWithEmailAndPassword, signOut} from "firebase/auth";
import {
  Alert,
  AppBar, Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Toolbar,
  Typography
} from "@mui/material";
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LoginForm from "./components/LoginForm";
import UserCard from "./components/UserCard";
import AdvancedFilters from "./components/AdvancedFilters";

function App() {
  const [authState, setAuthState] = useState({
    checked: false,    // Whether auth check completed
    user: null         // Current user
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [paymentDates, setPaymentDates] = useState({});
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [paymentHistories, setPaymentHistories] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [paymentToClear, setPaymentToClear] = useState(null);
  const [filters, setFilters] = useState({
    paymentStatus: 'all',
    country: ''
  });

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setAuthState({
        checked: true,
        user
      });

      if (user) {
        // Only load data if user is authenticated
        await loadAllData();
      }
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // 1. Load all users in one request
      const usersSnapshot = await getDocs(collection(db, "users"));
      const userList = usersSnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));

      // 2. Load ALL payment histories in parallel
      const paymentPromises = userList.map(user =>
        getDocs(collection(db, "users", user.id, "payments"))
          .then(snap => snap.docs.map(d => ({id: d.id, ...d.data()})))
          .catch(() => [])
      );

      const allPayments = await Promise.all(paymentPromises);
      // 3. Combine into single state update
      const paymentHistories = {};
      userList.forEach((user, i) => {
        paymentHistories[user.id] = allPayments[i];
      });

      setUsers(userList);
      setPaymentHistories(paymentHistories);
    } catch (error) {
      console.error("Load error:", error);
      setError("Failed to load data");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    try {
      setError(null);

      if (!email || !password) {
        setError("Please enter both email and password");
        setOpenSnackbar(true);
        return;
      }

      if (!recaptchaToken) {
        setError("Please complete the CAPTCHA verification");
        setOpenSnackbar(true);
        return;
      }

      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      let errorMessage = "Login failed";
      switch (error.code) {
        case "auth/invalid-email":
          errorMessage = "Invalid email address";
          break;
        case "auth/user-disabled":
          errorMessage = "Account disabled";
          break;
        case "auth/user-not-found":
          errorMessage = "User not found";
          break;
        case "auth/wrong-password":
          errorMessage = "Incorrect password";
          break;
        default:
          errorMessage = error.message;
      }
      setError(errorMessage);
      setOpenSnackbar(true);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setAuthState({ checked: true, user: null });
  };

  const updatePayment = async (userId) => {
    if (!paymentDates[userId]) {
      setError("Please select a date first");
      setOpenSnackbar(true);
      return;
    }

    try {
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error("User document not found");
      }

      // Convert JavaScript Date to Firestore Timestamp
      const timestamp = Timestamp.fromDate(paymentDates[userId]);

      // Add to payment history
      await addDoc(collection(db, "users", userId, "payments"), {
        date: timestamp,
        recordedAt: Timestamp.now(),
        type: "payment"
      });

      // Update current payment
      await updateDoc(userRef, {
        paymentDate: timestamp
      });

      // Refresh payment history for this user
      const paymentsCol = collection(db, "users", userId, "payments");
      const paymentsSnapshot = await getDocs(paymentsCol);
      setPaymentHistories(prev => ({
        ...prev,
        [userId]: paymentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      }));

      setPaymentDates(prev => ({...prev, [userId]: null}));
      setError("Payment updated successfully!");
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Payment update error:", error);
      setError(error.message || "Failed to update payment");
      setOpenSnackbar(true);
    }
  };

  const handleClearPayment = (userId, paymentId = null) => {
    setPaymentToClear({userId, paymentId});
    setConfirmDialogOpen(true);
  };

  const confirmClearPayment = async () => {
    try {
      const {userId, paymentId} = paymentToClear;
      const userRef = doc(db, "users", userId);

      if (paymentId) {
        // Delete specific payment record
        await deleteDoc(doc(db, "users", userId, "payments", paymentId));
      } else {
        // Clear current payment
        await updateDoc(userRef, {
          paymentDate: null
        });
      }

      // Reload payment history
      const paymentsCol = collection(db, "users", userId, "payments");
      const paymentsSnapshot = await getDocs(paymentsCol);
      setPaymentHistories(prev => ({
        ...prev,
        [userId]: paymentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      }));

      setError(paymentId ? "Payment record deleted" : "Payment cleared successfully!");
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Clear payment error:", error);
      setError(error.message || "Failed to clear payment");
      setOpenSnackbar(true);
    } finally {
      setConfirmDialogOpen(false);
      setPaymentToClear(null);
    }
  };

  const highlightMatch = React.useMemo(() => {
    return (text, search) => {
      if (!text || !search) return text;
      try {
        const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedSearch})`, 'gi');
        return text.toString().replace(regex, '<mark>$1</mark>');
      } catch (e) {
        console.error("Error in highlightMatch:", e);
        return text;
      }
    };
  }, []);

  const filterOptions = React.useMemo(() => {
    const countries = [...new Set(users.map(u => u.country).filter(Boolean))];
    return { countries };
  }, [users]);

  const filteredUsers = React.useMemo(() => {
    // Define getCurrentPaymentStatus inside useMemo to avoid dependency
    const getCurrentPaymentStatus = (userId) => {
      const history = paymentHistories[userId] || [];
      const sorted = [...history].sort((a, b) => {
        const dateA = a.date && a.date.toDate ? a.date.toDate() : new Date(0);
        const dateB = b.date && b.date.toDate ? b.date.toDate() : new Date(0);
        return dateB - dateA;
      });
      return sorted.length > 0 ? sorted[0] : null;
    };

    if (!authState.user) return [];
    return users.filter(user => {
      // Search term filtering
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const searchFields = [
          user.id,
          user.email,
          user.name,
          user.nameShort,
          user.contact,
          user.style,
          user.address1,
          user.address2,
          user.country,
          user.telephone
        ].filter(Boolean);
        if (!searchFields.some(field => field.toLowerCase().includes(searchLower))) {
          return false;
        }
      }

      // Payment status filter
      const payment = getCurrentPaymentStatus(user.id);
      const now = new Date();
      let paymentDate, expiryDate;

      if (payment?.type === "payment" && payment?.date) {
        paymentDate = payment.date.toDate ? payment.date.toDate() : new Date(0);
        expiryDate = new Date(paymentDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        expiryDate.setDate(expiryDate.getDate() - 1);
      }

      const isPaid = payment?.type === "payment" && payment?.date && now >= paymentDate && now <= expiryDate;
      const isFuture = payment?.type === "payment" && payment?.date && now < paymentDate;
      const isUnpaid = !isPaid && !isFuture;

      if (filters.paymentStatus === 'paid' && !isPaid) return false;
      if (filters.paymentStatus === 'unpaid' && !isUnpaid) return false;
      if (filters.paymentStatus === 'future' && !isFuture) return false;

      // Country filter
      if (filters.country && user.country !== filters.country) return false;

      return true;
    });
  }, [users, searchTerm, filters, authState.user, paymentHistories]);

  if (!authState.checked || loading) {
    return (
      <Container style={{display: 'flex', justifyContent: 'center', marginTop: '4rem'}}>
        <CircularProgress size={60}/>
      </Container>
    );
  }

  if (!authState.user) {
    return (
      <LoginForm
        email={email}
        password={password}
        recaptchaToken={recaptchaToken}
        onEmailChange={(e) => setEmail(e.target.value)}
        onPasswordChange={(e) => setPassword(e.target.value)}
        onRecaptchaChange={(token) => setRecaptchaToken(token)}
        onLogin={login}
        error={error}
        openSnackbar={openSnackbar}
        onCloseSnackbar={() => setOpenSnackbar(false)}
      />
    );
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{flexGrow: 1}}>
            WKA Membership Manager
          </Typography>
          <Button color="inherit" onClick={logout}>Logout</Button>
        </Toolbar>
      </AppBar>

      <Container sx={{marginTop: "2rem", marginBottom: "2rem"}}>
        <TextField
          label="Search clubs..."
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon/>
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                  edge="end"
                  aria-label="clear search"
                >
                  <ClearIcon fontSize="small"/>
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <AdvancedFilters
          filters={filters}
          setFilters={setFilters}
          filterOptions={filterOptions}
        />

        <Box sx={{ mb: 2, fontStyle: 'italic' }}>
          <Typography variant="body2" color="text.secondary">
            Showing: {filters.paymentStatus !== 'all' ? filters.paymentStatus : 'all'} members
            {filters.country && ` from ${filters.country}`}
          </Typography>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={3}>
            {filteredUsers.map(user => (
              <Grid item xs={12} sm={6} md={4} key={user.id}>
                <UserCard
                  user={user}
                  searchTerm={searchTerm}
                  paymentDate={paymentDates[user.id]}
                  paymentHistories={paymentHistories[user.id] || []}
                  onDateChange={(newDate) => setPaymentDates(prev => ({...prev, [user.id]: newDate}))}
                  onUpdatePayment={() => updatePayment(user.id)}
                  onClearPayment={(paymentId) => handleClearPayment(user.id, paymentId)}
                  highlightMatch={highlightMatch}
                />
              </Grid>
            ))}
          </Grid>
        </LocalizationProvider>
      </Container>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {paymentToClear?.paymentId
              ? "Are you sure you want to delete this payment record?"
              : "Are you sure you want to clear the current payment?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmClearPayment}
            color="error"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      >
        <Alert
          severity={error?.includes("success") ? "success" : "error"}
          onClose={() => setOpenSnackbar(false)}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

export default App;